import Event from "@Helpers/event";
import * as t from "io-ts";

// Base event interface that all events must implement
export interface BaseEvent {
  type: string;
  timestamp: number;
}

// Generic event emitter class that can handle any event type
export class EventEmitter<TEventType extends t.Mixed, TEvent extends BaseEvent> {
  private event: Event<TEvent>;
  private eventCodec: TEventType;

  constructor(eventCodec: TEventType) {
    this.event = new Event<TEvent>();
    this.eventCodec = eventCodec;
  }

  public on(callback: (event: TEvent) => void) {
    this.event.on(callback);
    return () => this.event.remove(callback);
  }

  public emit(event: TEvent) {
    // Validate event against codec
    const result = this.eventCodec.decode(event);
    if (result._tag === "Right") {
      this.event.emit(event);
    } else {
      console.error("Invalid event payload:", result.left);
    }
  }

  public remove(callback: (event: TEvent) => void) {
    this.event.remove(callback);
  }
}

// Event registry to manage all event types in the application
export class EventRegistry {
  private static instance: EventRegistry;
  private eventEmitters: Map<string, EventEmitter<any, any>>;

  private constructor() {
    this.eventEmitters = new Map();
  }

  public static getInstance(): EventRegistry {
    if (!EventRegistry.instance) {
      EventRegistry.instance = new EventRegistry();
    }
    return EventRegistry.instance;
  }

  public registerEventType<TEventType extends t.Mixed, TEvent extends BaseEvent>(
    namespace: string,
    eventCodec: TEventType
  ): EventEmitter<TEventType, TEvent> {
    if (!this.eventEmitters.has(namespace)) {
      this.eventEmitters.set(namespace, new EventEmitter<TEventType, TEvent>(eventCodec));
    }
    return this.eventEmitters.get(namespace) as EventEmitter<TEventType, TEvent>;
  }

  public getEventEmitter<TEventType extends t.Mixed, TEvent extends BaseEvent>(namespace: string): EventEmitter<TEventType, TEvent> | undefined {
    return this.eventEmitters.get(namespace) as EventEmitter<TEventType, TEvent>;
  }
}

// Export singleton instance
export const eventRegistry = EventRegistry.getInstance();
